"use client";

import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { useState, useEffect } from "react";
import Image from "next/image";
import { Menu, X, MapPin } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/components/ThemeProvider";
import CartButton from "../ui/CartButton";

const navLinks = [
  { name: "About", href: "#about" },
  { name: "Workspaces", href: "#workspaces" },
  { name: "Offers", href: "#offers" },
  { name: "Cafe", href: "#cafe" },
  { name: "Membership", href: "#membership" },
  { name: "Events", href: "#events" },
  { name: "Contact", href: "#cta" },
];

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme } = useTheme();
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");

  // Determine the actual theme (resolve "system" to light or dark)
  useEffect(() => {
    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      setResolvedTheme(systemTheme);

      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = (e: MediaQueryListEvent) => {
        setResolvedTheme(e.matches ? "dark" : "light");
      };
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    } else {
      setResolvedTheme(theme as "light" | "dark");
    }
  }, [theme]);

  const logoSrc =
    resolvedTheme === "dark"
      ? "/images/IDWorksLogo.svg"
      : "/images/idworks-logo.png";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    if (isOpen) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.body.style.position = "relative";
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.style.position = "";
    }

    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.style.position = "";
    };
  }, [isOpen]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Navigation Bar */}
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled
            ? "shadow-lg backdrop-blur-xl bg-card/95 dark:bg-card/95 border-b border-border"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.a
              href="#"
              className="flex items-center relative z-[102]"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
                setIsOpen(false);
              }}
            >
              <Image
                src={logoSrc}
                alt="idworks Hub"
                width={120}
                height={60}
                className="w-auto h-10 sm:h-12"
                priority
              />
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-heading hover:text-primary transition-colors duration-300 font-medium text-sm xl:text-base"
                >
                  {link.name}
                </a>
              ))}

              {/* Theme Toggle for Desktop */}
              <ThemeToggle />

              {/* Cart Button for Desktop */}
              <CartButton />
            </div>

            {/* Mobile: Cart + Theme Toggle + Menu Button */}
            <div className="lg:hidden flex items-center gap-2 sm:gap-3 relative z-[102]">
              {/* Cart Button for Mobile */}
              <CartButton />

              {/* Theme Toggle for Mobile */}
              <ThemeToggle />

              <motion.button
                onClick={() => setIsOpen(!isOpen)}
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-card backdrop-blur-xl border border-border flex items-center justify-center text-heading shadow-sm hover:bg-muted transition-all"
                aria-label={isOpen ? "Close menu" : "Open menu"}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <AnimatePresence mode="wait">
                  {isOpen ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[101] lg:hidden"
              onClick={() => setIsOpen(false)}
            />

            {/* Slide-in Menu Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-full sm:w-[400px] bg-card dark:bg-card shadow-2xl z-[101] lg:hidden overflow-y-auto"
            >
              {/* Menu Header */}
              <div className="sticky top-0 bg-card backdrop-blur-xl border-b border-border px-6 sm:px-8 py-6 z-10">
                <div className="flex items-center justify-between">
                  <Image
                    src={logoSrc}
                    alt="idworks Hub"
                    width={100}
                    height={50}
                    className="w-auto h-10"
                  />
                  <motion.button
                    onClick={() => setIsOpen(false)}
                    className="w-10 h-10 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center text-heading transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Close menu"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              {/* Menu Content */}
              <div className="px-6 sm:px-8 py-8">
                {/* Navigation Links */}
                <nav className="space-y-1">
                  {navLinks.map((link, index) => (
                    <motion.a
                      key={link.name}
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      initial={{ x: 50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{
                        delay: index * 0.05 + 0.1,
                        duration: 0.3,
                      }}
                      className="group block px-5 py-4 rounded-xl text-heading hover:bg-primary/5 hover:text-primary transition-all duration-300 font-medium text-lg relative overflow-hidden"
                    >
                      {/* Hover effect */}
                      <span className="absolute left-0 top-0 bottom-0 w-1 bg-primary scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top rounded-r-full" />
                      <span className="relative z-10">{link.name}</span>
                    </motion.a>
                  ))}
                </nav>

                {/* Divider */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                  className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-8"
                />

                {/* Location Info */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.3 }}
                  className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-6 border border-primary/10"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-heading font-bold text-lg mb-1">
                        Visit Us
                      </h3>
                      <p className="text-text text-sm leading-relaxed">
                        KAFD, Riyadh
                        <br />
                        <span className="text-xs text-text/70">
                          King Abdullah Financial District
                        </span>
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Social Links or Additional Info */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7, duration: 0.3 }}
                  className="mt-6 text-center"
                >
                  <p className="text-text/60 text-sm">
                    © 2024 idworks Hub. All rights reserved.
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
